import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";
import { normalizeHexColor } from "../utils/colorUtils";

const UiPreferencesContext = createContext(null);
const API_URL = import.meta.env.VITE_API_URL || "https://api.endoac.ro";

const normalizePrefs = (raw) => ({
  userColors: raw?.userColors && typeof raw.userColors === "object" ? raw.userColors : {},
  categoryColors: raw?.categoryColors && typeof raw.categoryColors === "object" ? raw.categoryColors : {},
});

export function UiPreferencesProvider({ children }) {
  const { token, currentUser } = useAuth();
  const [prefs, setPrefs] = useState(() => normalizePrefs(null));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const authHeaders = useMemo(() => {
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  }, [token]);

  const fetchPreferences = useCallback(async () => {
    if (!token || currentUser?.role !== "admin") {
      setPrefs(normalizePrefs(null));
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/users/me/ui-preferences`, {
        headers: {
          ...authHeaders,
        },
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Nu s-au putut încărca preferințele UI");
      }
      setPrefs(normalizePrefs(data?.data?.uiPreferences));
    } catch (err) {
      console.error("Eroare preferințe UI:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, currentUser?.role, token]);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const updatePreferences = useCallback(
    async (payload) => {
      if (!token || currentUser?.role !== "admin") return null;
      const res = await fetch(`${API_URL}/api/users/me/ui-preferences`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Nu s-au putut salva preferințele UI");
      }
      const nextPrefs = normalizePrefs(data?.data?.uiPreferences);
      setPrefs(nextPrefs);
      return nextPrefs;
    },
    [authHeaders, currentUser?.role, token]
  );

  const setUserColor = useCallback(
    async (userId, color) => {
      if (!userId) return null;
      const normalized = normalizeHexColor(color);
      const payload = {
        userColors: {
          [userId]: normalized || null,
        },
      };
      const optimistic = {
        ...prefs,
        userColors: {
          ...prefs.userColors,
          [userId]: normalized || undefined,
        },
      };
      if (!normalized) {
        delete optimistic.userColors[userId];
      }
      setPrefs(optimistic);
      try {
        return await updatePreferences(payload);
      } catch (err) {
        setPrefs(prefs);
        throw err;
      }
    },
    [prefs, updatePreferences]
  );

  const setCategoryColor = useCallback(
    async (category, color) => {
      const key = (category || "").trim();
      if (!key) return null;
      const normalized = normalizeHexColor(color);
      const payload = {
        categoryColors: {
          [key]: normalized || null,
        },
      };
      const optimistic = {
        ...prefs,
        categoryColors: {
          ...prefs.categoryColors,
          [key]: normalized || undefined,
        },
      };
      if (!normalized) {
        delete optimistic.categoryColors[key];
      }
      setPrefs(optimistic);
      try {
        return await updatePreferences(payload);
      } catch (err) {
        setPrefs(prefs);
        throw err;
      }
    },
    [prefs, updatePreferences]
  );

  const value = useMemo(
    () => ({
      userColors: prefs.userColors || {},
      categoryColors: prefs.categoryColors || {},
      loading,
      error,
      refresh: fetchPreferences,
      setUserColor,
      setCategoryColor,
    }),
    [prefs, loading, error, fetchPreferences, setUserColor, setCategoryColor]
  );

  return <UiPreferencesContext.Provider value={value}>{children}</UiPreferencesContext.Provider>;
}

export const useUiPreferences = () => useContext(UiPreferencesContext);
